import { useMemo } from 'react';
import LinkIcon from '@mui/icons-material/Link';
import { Box, Chip, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { Link } from 'react-router-dom';
import { DataTable, PageHeader, StatusChip, LoadingState, SectionCard } from '@/components/shared';
import { PipedriveDeal } from '@/services/pipedrive';
import { Column } from '@/trio-ui/DataTable';

export type DatePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

export type DateFilter = {
  preset: DatePreset;
  startDate: string;
  endDate: string;
};

type Props = {
  isPipedriveConnected: boolean;
  deals: PipedriveDeal[];
  isLoading: boolean;
  dateFilter: DateFilter;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDateChange: (startDate: string, endDate: string) => void;
};

const fmt = (amount: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

const STATUS_MAP = {
  open: { label: 'Open', color: '#054AD9' },
  won: { label: 'Won', color: '#10B981' },
  lost: { label: 'Lost', color: '#EF4444' },
  deleted: { label: 'Deleted', color: '#71717A' },
};

const STATUS_CHART_COLORS: Record<string, string> = {
  open: '#054AD9',
  won: '#10B981',
  lost: '#EF4444',
};

const columns: Column<PipedriveDeal>[] = [
  {
    id: 'title',
    label: 'Deal',
    render: (row) => (
      <Box>
        <Box sx={{ fontWeight: 500, fontSize: 13 }}>{row.title}</Box>
        {row.orgName && <Box sx={{ fontSize: 12, color: '#71717A' }}>{row.orgName}</Box>}
      </Box>
    ),
  },
  {
    id: 'value',
    label: 'Value',
    align: 'right',
    render: (row) => (
      <Box sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: 13 }}>{fmt(row.value)}</Box>
    ),
  },
  {
    id: 'stageName',
    label: 'Stage',
    render: (row) => (
      <Chip
        label={row.stageName}
        size="small"
        sx={{
          height: 24,
          fontSize: 12,
          fontWeight: 500,
          backgroundColor: '#F4F4F5',
          color: '#4A4B4E',
        }}
      />
    ),
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (row) => <StatusChip status={row.status} statusMap={STATUS_MAP} />,
  },
  {
    id: 'personName',
    label: 'Contact',
    render: (row) => row.personName ?? '—',
  },
  {
    id: 'addTime',
    label: 'Created',
    render: (row) => (row.addTime ? new Date(row.addTime).toLocaleDateString() : '—'),
  },
  {
    id: 'expectedCloseDate',
    label: 'Expected Close',
    render: (row) => row.expectedCloseDate ?? '—',
  },
];

const CEOxPipelineComponent = function ({
  isPipedriveConnected,
  deals,
  isLoading,
  dateFilter,
  onPresetChange,
  onCustomDateChange,
}: Props): JSX.Element {
  // --- Chart data: deals by status ---
  const statusChartData = useMemo(() => {
    const counts: Record<string, { count: number; value: number }> = {
      open: { count: 0, value: 0 },
      won: { count: 0, value: 0 },
      lost: { count: 0, value: 0 },
    };

    for (const d of deals) {
      if (counts[d.status]) {
        counts[d.status].count += 1;
        counts[d.status].value += d.value;
      }
    }

    return Object.entries(counts).map(([status, data]) => ({
      status,
      label: STATUS_MAP[status as keyof typeof STATUS_MAP]?.label ?? status,
      color: STATUS_CHART_COLORS[status] ?? '#71717A',
      ...data,
    }));
  }, [deals]);

  const statusBarOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) =>
          params
            .map((p: any) => `${p.marker} ${p.seriesName}: ${p.seriesName === 'Value' ? fmt(p.value) : p.value}`)
            .join('<br/>'),
      },
      legend: {
        data: ['Count', 'Value'],
        bottom: 0,
        textStyle: { fontSize: 12, color: '#71717A' },
      },
      grid: { left: 50, right: 60, top: 20, bottom: 44 },
      xAxis: {
        type: 'category',
        data: statusChartData.map((s) => s.label),
        axisLabel: { fontSize: 12, color: '#71717A' },
        axisLine: { lineStyle: { color: '#E4E4E7' } },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Deals',
          axisLabel: { fontSize: 11, color: '#71717A' },
          splitLine: { lineStyle: { color: '#F4F4F5' } },
        },
        {
          type: 'value',
          name: 'Value',
          axisLabel: { fontSize: 11, color: '#71717A', formatter: (v: number) => fmt(v) },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: 'Count',
          type: 'bar',
          data: statusChartData.map((s) => ({
            value: s.count,
            itemStyle: { color: s.color, borderRadius: [4, 4, 0, 0] },
          })),
          barWidth: 40,
        },
        {
          name: 'Value',
          type: 'bar',
          yAxisIndex: 1,
          data: statusChartData.map((s) => ({
            value: s.value,
            itemStyle: { color: `${s.color}60`, borderRadius: [4, 4, 0, 0] },
          })),
          barWidth: 40,
        },
      ],
    }),
    [statusChartData]
  );

  // --- Date filter UI ---
  const dateFilterActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <ToggleButtonGroup
        value={dateFilter.preset}
        exclusive
        onChange={(_, val) => val && onPresetChange(val)}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontSize: 13,
            px: 1.5,
            py: 0.5,
            borderColor: '#E4E4E7',
            '&.Mui-selected': {
              backgroundColor: '#054AD915',
              color: '#054AD9',
              borderColor: '#054AD9',
              '&:hover': { backgroundColor: '#054AD925' },
            },
          },
        }}
      >
        <ToggleButton value="this_month">This Month</ToggleButton>
        <ToggleButton value="last_month">Last Month</ToggleButton>
        <ToggleButton value="this_quarter">This Quarter</ToggleButton>
        <ToggleButton value="this_year">YTD</ToggleButton>
        <ToggleButton value="last_year">Last Year</ToggleButton>
        <ToggleButton value="custom">Custom</ToggleButton>
      </ToggleButtonGroup>
      {dateFilter.preset === 'custom' && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            type="date"
            size="small"
            value={dateFilter.startDate}
            onChange={(e) => onCustomDateChange(e.target.value, dateFilter.endDate)}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
          />
          <TextField
            type="date"
            size="small"
            value={dateFilter.endDate}
            onChange={(e) => onCustomDateChange(dateFilter.startDate, e.target.value)}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
          />
        </Box>
      )}
    </Box>
  );

  if (!isPipedriveConnected) {
    return (
      <>
        <PageHeader title="Pipeline" />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            borderRadius: '8px',
            backgroundColor: '#FEF3C7',
            border: '1px solid #FDE68A',
          }}
        >
          <LinkIcon sx={{ fontSize: 18, color: '#D97706' }} />
          <Box sx={{ fontSize: 13, color: '#92400E', flex: 1 }}>
            Connect your <strong>Pipedrive</strong> account to view your sales pipeline.
          </Box>
          <Link
            to="/settings/integrations"
            style={{ fontSize: 13, color: '#054AD9', textDecoration: 'none', fontWeight: 500 }}
          >
            Connect now
          </Link>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Pipeline" actions={dateFilterActions} />
        <LoadingState message="Loading deals..." />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Pipeline" actions={dateFilterActions} />

      {/* Deals by Status chart */}
      <Box sx={{ mb: 3 }}>
        <SectionCard
          title="Deals by Status"
          subtitle={`${deals.length} deal${deals.length !== 1 ? 's' : ''} in selected period`}
        >
          {deals.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', fontSize: 13, color: '#71717A' }}>
              No deals found for this period.
            </Box>
          ) : (
            <ReactECharts option={statusBarOption} style={{ height: 280 }} />
          )}
        </SectionCard>
      </Box>

      {/* Deals table */}
      <DataTable
        columns={columns}
        data={deals}
        rowKey={(r) => String(r.id)}
        searchPlaceholder="Search deals..."
        searchFields={(r) => `${r.title} ${r.orgName ?? ''} ${r.personName ?? ''} ${r.stageName}`}
        emptyMessage="No deals found for this period."
      />
    </>
  );
};

export default CEOxPipelineComponent;
